import { parseMoneyToCents, tt } from '@agos/shared'

/**
 * The two campaigns that change what an order costs — as the **customer's summary** shows them.
 *
 * `GET /campaigns/active-promo` answers a *Black Friday* discount and a *free delivery* weekend, either
 * of which may be null. Measured on this data both are: nothing is configured today, which is why this
 * was filed rather than built (FB-C44) and why it is built now — the wheel had `is_active: false` too,
 * and a franchise turning one on is a settings toggle, not a release.
 *
 * **This computes what to display, and nothing else is sent.** The server decides the discount:
 * `OrderCampaigns` evaluates both at order creation, written that way precisely because legacy applied
 * Black Friday in the browser alone and stored the undiscounted total — the order was saved for more
 * than the customer believed they were paying. Legacy still posts `promo: {blackFridayAmount,
 * freeDelivery}` in the order body, which is the client telling the server what to charge. **This port
 * does not send it.** The arithmetic here has to agree with the server's, and if it ever disagrees the
 * server wins, which is the right way round.
 *
 * So the gap this closes is narrower than legacy's feature and more honest: without it the summary
 * quotes a total **higher** than the customer will be charged, and a free-delivery weekend looks like
 * an ordinary delivery fee.
 */

export interface BlackFriday {
  discountType: 'percent' | 'fixed'
  /** Percent as a number of percent; fixed as cents. */
  discountValue: number
  minOrderCents: number
  label: string
  stackableWithCoupons: boolean
}

export interface FreeDelivery {
  minOrderCents: number
  label: string
}

export interface ActivePromo {
  blackFriday: BlackFriday | null
  freeDelivery: FreeDelivery | null
}

function label(row: Record<string, unknown>, fallback: string): string {
  const value = row.promoLabel
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback
}

export function activePromoOf(payload: unknown): ActivePromo {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>

  let blackFriday: BlackFriday | null = null
  if (typeof row.blackFriday === 'object' && row.blackFriday !== null) {
    const card = row.blackFriday as Record<string, unknown>
    const type = card.discountType === 'fixed' ? 'fixed' : 'percent'
    const raw = card.discountValue
    const value =
      typeof raw === 'number' || typeof raw === 'string'
        ? type === 'fixed'
          ? parseMoneyToCents(raw)
          : Number(raw)
        : 0
    // A campaign worth nothing is not a campaign, and a line reading *−0,00 €* is worse than no line.
    if (Number.isFinite(value) && value > 0) {
      blackFriday = {
        discountType: type,
        discountValue: value,
        minOrderCents: parseMoneyToCents((card.minOrderValue ?? 0) as string | number),
        label: label(card, tt('Akcia')),
        // Absent means stackable — the server's own default.
        stackableWithCoupons: card.stackableWithCoupons !== false,
      }
    }
  }

  let freeDelivery: FreeDelivery | null = null
  if (typeof row.freeDelivery === 'object' && row.freeDelivery !== null) {
    const card = row.freeDelivery as Record<string, unknown>
    freeDelivery = {
      minOrderCents: parseMoneyToCents((card.minOrderValue ?? 0) as string | number),
      label: label(card, tt('Doprava zdarma')),
    }
  }

  return { blackFriday, freeDelivery }
}

/**
 * What Black Friday takes off, in cents.
 *
 * Legacy's three conditions, in its order: the campaign exists, the basket reaches its minimum, and
 * either it stacks with codes or no code is applied. A percentage is taken of the **subtotal**, and a
 * fixed amount can never exceed it — nobody is paid to collect an order.
 */
export function blackFridayCents(
  promo: ActivePromo,
  subtotalCents: number,
  codeApplied: boolean,
): number {
  const campaign = promo.blackFriday
  if (campaign === null) return 0
  if (subtotalCents < campaign.minOrderCents) return 0
  if (!campaign.stackableWithCoupons && codeApplied) return 0
  if (campaign.discountType === 'percent') {
    return Math.round((subtotalCents * campaign.discountValue) / 100)
  }
  return Math.min(subtotalCents, campaign.discountValue)
}

/** Whether the delivery fee is waived — delivery orders only, above the campaign's minimum. */
export function freeDeliveryApplies(
  promo: ActivePromo,
  subtotalCents: number,
  isDelivery: boolean,
): boolean {
  const campaign = promo.freeDelivery
  return campaign !== null && isDelivery && subtotalCents >= campaign.minOrderCents
}

/** `🛍️ Black Friday (-20 %)` — the campaign's own name, and the rate when it has one. */
export function blackFridayLabel(promo: ActivePromo): string {
  const campaign = promo.blackFriday
  if (campaign === null) return ''
  const rate =
    campaign.discountType === 'percent' ? ` (-${String(campaign.discountValue)} %)` : ''
  return `🛍️ ${campaign.label}${rate}`
}

/**
 * Happy hours — §9.254, FB-414, and Black Friday's sibling.
 *
 * `GET /campaigns/happy-hours/public` answers `{isActive, discountPercent, bonusPointsMultiplier}`
 * and **nothing called it**, while `OrderCreationService` has always read `campaigns.happyHours()`
 * and applied it to both the discount and the loyalty multiplier. So the same shape FB-C44 described
 * for Black Friday: the order is discounted whether or not the summary mentions it.
 *
 * Measured today: `{"isActive": false, "discountPercent": 0, "bonusPointsMultiplier": 1}` — off, so
 * client and server agree at nought and nothing looks wrong. They disagree from the moment it is
 * switched on, which is a settings toggle rather than a release.
 *
 * **The percentage is the server's and the arithmetic is the same as Black Friday's**, deliberately:
 * two ways of taking a percentage off a subtotal is how a preview drifts from a bill.
 */
export interface HappyHours {
  discountPercent: number
  /** What an order earns during the hour, as a multiple. `1` means the ordinary rate. */
  bonusPointsMultiplier: number
}

export function happyHoursOf(payload: unknown): HappyHours | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (row.isActive !== true) return null
  const percent = typeof row.discountPercent === 'number' ? row.discountPercent : 0
  const multiplier =
    typeof row.bonusPointsMultiplier === 'number' && row.bonusPointsMultiplier > 0
      ? row.bonusPointsMultiplier
      : 1
  // Active with nothing to give is not an offer. A *Happy Hours (0 %)* line is worse than none, and
  // a multiplier of one is the ordinary rate rather than a bonus.
  if (percent <= 0 && multiplier <= 1) return null
  return { discountPercent: percent, bonusPointsMultiplier: multiplier }
}

export function happyHoursCents(happy: HappyHours | null, subtotalCents: number): number {
  if (happy === null || happy.discountPercent <= 0) return 0
  return Math.round((subtotalCents * happy.discountPercent) / 100)
}

/** *Happy Hours (15 %)* — legacy's own line, `Checkout.tsx:2562`. */
export function happyHoursLabel(happy: HappyHours | null): string {
  if (happy === null || happy.discountPercent <= 0) return ''
  return `Happy Hours (${happy.discountPercent} %)`
}

/**
 * Said rather than computed — the restraint §9.218 used for the tier.
 *
 * `bonusPointsMultiplier` is applied by the server at order creation; predicting the figure here
 * would be a second arithmetic to keep in step for no gain.
 */
export function happyHoursPointsNote(happy: HappyHours | null): string {
  if (happy === null || happy.bonusPointsMultiplier <= 1) return ''
  return tt('Počas Happy Hours získate za objednávku viac vernostných bodov.')
}
